
import React, { useState, useEffect, useCallback, useRef } from "react";
import "../Style/ImmoblilizMobilize.css"; // Assuming this CSS file exists

// Define the base URL for your API
const API_BASE_URL = "https://api.yellowmatrix.co.in";
// Define logs per page as a constant
const LOGS_PER_PAGE = 10;

// Helper to format boolean API responses
const formatBool = (val) => (val ? "Success" : "Failed");
const formatAction = (val) => (val ? "ON" : "OFF");

// NEW: Default state for all GPIOs
const defaultOutputStates = {
  gpio_1: false,
  gpio_2: false,
  gpio_3: false,
  gpio_4: false,
};

export default function ImmobilizeMobilize() {
  const [selectedVehicle, setSelectedVehicle] = useState("");
  const [commandHistory, setCommandHistory] = useState([]);
  const [vehicleOptions, setVehicleOptions] = useState([]);
  
  // Loading states
  const [vehiclesLoading, setVehiclesLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [statusLoading, setStatusLoading] = useState(false); 
  const [loadingCommand, setLoadingCommand] = useState(null); 
  
  const [error, setError] = useState(null);
  const [apiFeedback, setApiFeedback] = useState({ message: "", type: "" });
  
  // Log pagination
  const [viewingLogsFor, setViewingLogsFor] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);

  // State for GPIO outputs
  const [outputStates, setOutputStates] = useState(defaultOutputStates);

  // State for custom dropdown
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const token = localStorage.getItem("token");

  // Fetches all available vehicles to populate the dropdowns
  const fetchVehicleOptions = useCallback(async () => {
    setVehiclesLoading(true);
    setError(null);
    try {
      
      const response = await fetch(`${API_BASE_URL}/vehicle/device/assign`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch vehicles: ${response.statusText}`);
      }
      const data = await response.json();
      const validVehicles = data.Vehicles || [];
      setVehicleOptions(validVehicles);
    } catch (err) {
      console.error("Error fetching vehicle options:", err);
      setError(err.message); // This error is critical, so we show it.
    } finally {
      setVehiclesLoading(false);
    }
  }, [token]);

  // Fetches GPIO status silently
  const fetchGpioStatus = useCallback(async (vehicleId) => {
    if (!vehicleId) {
      setOutputStates(defaultOutputStates);
      return;
    }
    setStatusLoading(true);
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/gpio/status?vehicle_id=${vehicleId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }

      const data = await response.json();
      
      if (data.gpio_status) {
        setOutputStates(data.gpio_status);
      } else {
         setOutputStates(defaultOutputStates);
      }

    } catch (err) {
      console.error("Error fetching GPIO status:", err.message);
      setOutputStates(defaultOutputStates);
    } finally {
      setStatusLoading(false);
    }
  }, [token]);

  // Fetches GPIO command logs
  const fetchGpioLogs = useCallback(async (vehicleId, page = 1) => {
    setHistoryLoading(true);
    setViewingLogsFor(vehicleId || null);
    setApiFeedback({ message: "", type: "" });

    try {
      let apiUrl = `${API_BASE_URL}/gpio/logs?page=${page}&per_page=${LOGS_PER_PAGE}`;
      if (vehicleId) {
        apiUrl = `${API_BASE_URL}/gpio/logs?vehicle_id=${vehicleId}&page=${page}&per_page=${LOGS_PER_PAGE}`;
      }

      const response = await fetch(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch history: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.Message === "No Logs Available") {
        setCommandHistory([]);
        setHasNextPage(false);
      } else {
        const logs = data["GPIO Logs"] || [];
        setCommandHistory(logs);
        setHasNextPage(logs.length === LOGS_PER_PAGE);
      }
      setCurrentPage(page);
    } catch (err) {
      console.error("Error fetching command history:", err);
      setApiFeedback({ message: `Logs Error: ${err.message}`, type: "error" });
    } finally {
      setHistoryLoading(false);
    }
  }, [token]);

  // Fetch initial vehicle list AND recent history on component mount
  useEffect(() => {
    fetchVehicleOptions();
    fetchGpioLogs(null, 1); // Fetch page 1 of all logs
  }, [fetchVehicleOptions, fetchGpioLogs]);

  // Unified function to handle all GPIO commands
  const handleGpioCommand = async (gpio_pin, state) => {
    if (!selectedVehicle) return;

    const commandId = `gpio_${gpio_pin}_${state}`;
    setLoadingCommand(commandId);
    setApiFeedback({ message: "", type: "" }); // Clear previous feedback

    try {
      const response = await fetch(`${API_BASE_URL}/gpio/${gpio_pin}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          vehicle_id: selectedVehicle,
          state: state, // API expects "ON" or "OFF"
        }),
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(
          result.Error || `Request failed with status ${response.status}`
        );
      }

      setApiFeedback({
        message: `GPIO ${gpio_pin} ${state} command successful!`,
        type: "success",
      });

      fetchGpioStatus(selectedVehicle);
      fetchGpioLogs(selectedVehicle, 1); 

    } catch (err) {
      console.error(`Error sending GPIO ${gpio_pin} ${state} command:`, err);
      setApiFeedback({ message: err.message, type: "error" });
      fetchGpioStatus(selectedVehicle);
    } finally {
      setLoadingCommand(null);
    }
  };

  // Handles selection from the custom dropdown
  const handleVehicleDropdownSelect = (vehicleId) => {
    setSelectedVehicle(vehicleId);
    setDropdownOpen(false);
    setSearchTerm("");
    setCurrentPage(1); 
    
    fetchGpioLogs(vehicleId, 1);
    fetchGpioStatus(vehicleId);
  };

  // --- Handlers for custom dropdown ---
  const toggleDropdown = () => {
    if (vehiclesLoading || isLoading) return;
    setDropdownOpen(!isDropdownOpen);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // --- Handler for page changes ---
  const handlePageChange = (newPage) => {
    if (newPage < 1) return;
    fetchGpioLogs(viewingLogsFor, newPage);
  };

  // --- Effect to close dropdown on outside click ---
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);


  const isLoading = !!loadingCommand; // True if any command is loading
  const isVehicleSelected = !!selectedVehicle;

  // Filtered vehicles for dropdown
  const filteredVehicleOptions = vehicleOptions.filter(vehicle =>
    vehicle.VehicleID.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <div className="container-Immobilize">
        <h2 id="Immobilize-H2-Immobilize">Immobilize / Mobilize Vehicle</h2>
        <p className="subtitle-Immobilize">
           Manage vehicle security through immobilization and reactivation.
        </p>
        <hr className="line-Immobilize" width="100%" />

        {error && (
          <div className="error-message-Immobilize">Error: {error}</div>
        )}
        {apiFeedback.message && (
          <div className={`api-feedback-Immobilize ${apiFeedback.type}`}>
            {apiFeedback.message}
          </div>
        )}

        <div className="card-2-Immobilize">
          <h3>Choose Vehicle to Control</h3>
          
          {/* Custom Dropdown */}
          <div className="custom-select-container-Immobilize" ref={dropdownRef}>
            <div
              className={`custom-select-value-Immobilize ${vehiclesLoading || isLoading ? 'disabled' : ''} ${isDropdownOpen ? 'open' : ''}`}
              onClick={toggleDropdown}
            >
              {selectedVehicle ? (
                <span>{selectedVehicle}</span>
              ) : (
                <div className="spacebetweet-Immobilize">
                <i className="fa-solid fa-magnifying-glass"></i><span>Select a vehicle</span>
                </div>
              )}
              <span className="arrow">▼</span>
            </div>

            {isDropdownOpen && (
              <div className="custom-select-dropdown-Immobilize">
                <input
                  type="text"
                  className="custom-select-search-Immobilize"
                  placeholder="Search vehicles..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  autoFocus
                />
                <ul className="custom-select-options-Immobilize">
                  {vehiclesLoading ? (
                    <li className="custom-select-option-Immobilize loading-option">Loading vehicles...</li>
                  ) : filteredVehicleOptions.length > 0 ? (
                    filteredVehicleOptions.map((vehicle) => (
                      <li
                        key={vehicle.VehicleID}
                        className={`custom-select-option-Immobilize ${selectedVehicle === vehicle.VehicleID ? 'selected' : ''}`}
                        onClick={() => handleVehicleDropdownSelect(vehicle.VehicleID)}
                      >
                        {vehicle.VehicleID}
                      </li>
                    ))
                  ) : (
                    <li className="custom-select-option-Immobilize loading-option">No vehicles found</li>
                  )}
                </ul>
              </div>
            )}
          </div>
          {/* END: Custom Dropdown */}


          {/* --- Command Grid --- */}
          <div className="command-grid-Immobilize">
            {/* GPIO 1 (Immobilize) Row */}
            <div className="command-row-Immobilize">
              <div>
                <span className="command-label-Immobilize">
                  GPIO 1 (Immobilize)
                </span>
                <p className="command-note-Immobilize">
                  Remotely enables (ON) or disables (OFF) the vehicle's engine.
                </p>
              </div>
              <div className="command-buttons-Immobilize">
                <label className="toggle-switch-Immobilize">
                  <input
                    type="checkbox"
                    checked={outputStates.gpio_1}
                    onChange={() =>
                      handleGpioCommand(1, outputStates.gpio_1 ? "OFF" : "ON")
                    }
                    disabled={!isVehicleSelected || isLoading || statusLoading}
                  />
                  <span className="slider-Immobilize">
                    <span className="on-text-Immobilize">On</span>
                    <span className="off-text-Immobilize">Off</span>
                  </span>
                </label>
              </div>
            </div>

            {/* GPIO 2 Row */}
            <div className="command-row-Immobilize">
              <div>
                <span className="command-label-Immobilize">
                  GPIO 2
                </span>
                <p className="command-note-Immobilize">
                  Activates (ON) or deactivates (OFF) the second digital output.
                </p>
              </div>
              <div className="command-buttons-Immobilize">
                <label className="toggle-switch-Immobilize">
                  <input
                    type="checkbox"
                    checked={outputStates.gpio_2}
                    onChange={() =>
                      handleGpioCommand(2, outputStates.gpio_2 ? "OFF" : "ON")
                    }
                    disabled={!isVehicleSelected || isLoading || statusLoading}
                  />
                  <span className="slider-Immobilize">
                    <span className="on-text-Immobilize">On</span>
                    <span className="off-text-Immobilize">Off</span>
                  </span>
                </label>
              </div>
            </div>

            {/* GPIO 3 Row */}
            <div className="command-row-Immobilize">
              <div>
                <span className="command-label-Immobilize">
                  GPIO 3
                </span>
                <p className="command-note-Immobilize">
                  Activates (ON) or deactivates (OFF) the third digital output.
                </p>
              </div>
              <div className="command-buttons-Immobilize">
                <label className="toggle-switch-Immobilize">
                  <input
                    type="checkbox"
                    checked={outputStates.gpio_3}
                    onChange={() =>
                      handleGpioCommand(3, outputStates.gpio_3 ? "OFF" : "ON")
                    }
                    disabled={!isVehicleSelected || isLoading || statusLoading}
                  />
                  <span className="slider-Immobilize">
                    <span className="on-text-Immobilize">On</span>
                    <span className="off-text-Immobilize">Off</span>
                  </span>
                </label>
              </div>
            </div>

            {/* GPIO 4 Row */}
            <div className="command-row-Immobilize">
              <div>
                <span className="command-label-Immobilize">
                  GPIO 4
                </span>
                <p className="command-note-Immobilize">
                  Activates (ON) or deactivates (OFF) the fourth digital output.
                </p>
              </div>
              <div className="command-buttons-Immobilize">
                <label className="toggle-switch-Immobilize">
                  <input
                    type="checkbox"
                    checked={outputStates.gpio_4}
                    onChange={() =>
                      handleGpioCommand(4, outputStates.gpio_4 ? "OFF" : "ON")
                    }
                    disabled={!isVehicleSelected || isLoading || statusLoading}
                  />
                  <span className="slider-Immobilize">
                    <span className="on-text-Immobilize">On</span>
                    <span className="off-text-Immobilize">Off</span>
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* --- Command History Section --- */}
        <div className="container-Command-History-Immobilize">
          <h3 id="command-history-H3-Immobilize">
            Command History {viewingLogsFor && `for ${viewingLogsFor}`}
          </h3>
          <div className="table-container-Immobilize">
            <table className="table-Immobilize">
              <thead>
                <tr>
                  <th className="th-Immobilize">Serial No.</th>
                  <th className="th-Immobilize">Vehicle Number</th>
                  <th className="th-Immobilize">Command</th>
                  <th className="th-Immobilize">Status</th>
                  <th className="th-Immobilize">Performed At</th>
                  <th className="th-Immobilize">Perform By</th>
                </tr>
              </thead>
              <tbody>
                {historyLoading ? (
                  <tr>
                    <td colSpan="6" className="text-center">
                      Loading history...
                    </td>
                  </tr>
                ) : commandHistory.length > 0 ? (
                  commandHistory.map((row, index) => {
                    const serialNo =
                      (currentPage - 1) * LOGS_PER_PAGE + index + 1;
                    
                    // --- *** UPDATED LOGIC HERE *** ---
                    let command;
                    if (row.gpio_pin === 1) {
                      // Use Mobilize/Immobilize only for GPIO 1
                      command = row.action_type ? "Mobilize" : "Immobilize";
                    } else {
                      // Use standard format for GPIO 2, 3, 4
                      command = `GPIO ${row.gpio_pin}: ${formatAction(row.action_type)}`;
                    }
                    // --- *** END OF UPDATE *** ---

                    const status = formatBool(row.action_status);

                    return (
                      <tr key={row.id}>
                        <td className="td-Immobilize">{serialNo}</td>
                        <td className="td-Immobilize">{row.vehicleid}</td>
                        <td className="td-Immobilize">{command}</td>
                        <td className="td-Immobilize">
                          <span
                            className={`status-badge-Immobilize ${status.toLowerCase()}`}
                          >
                            {status}
                          </span>
                        </td>
                        <td className="td-Immobilize">
                          {new Date(row.performedat).toLocaleString()}
                        </td>
                        
                        <td className="td-Immobilize">
                          {row?.user_details?.username || "—"}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                <tr>
                  <td colSpan="6" className="text-center">
                    {viewingLogsFor ? 'No logs available for this vehicle.' : 'No recent command history available.'}
                  </td>
                </tr>
              )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="pagination-controls-Immobilize">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || historyLoading}
            >
              Previous
            </button>
            <span>Page {currentPage}</span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!hasNextPage || historyLoading}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </>
  );
}